import React, { FC, useMemo, useRef } from 'react'
import { ProfileOverview } from '../organisms'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useWindowSizeUp } from '../../lib/utils'
import { makeStyles } from 'tss-react/mui'
import classNames from 'classnames'
import { colors, RPUser } from '@hazadapt-git/public-core-base'
import { useElementWidthUp } from '../../lib/hooks/useElementMediaQuery'
import { useScrollHighlight } from '../../lib/hooks/useScrollHighlight'
import { ProfileDetailsForm } from '../organisms/ProfileDetailsForm'
import { FormikProps } from 'formik'
import { FormikState } from '../../lib/hooks/useFormikStateSync'
import { LoadingButton } from '../atoms'
import { ProfileFormMode } from '../../lib/entities'

interface ManageProfilePageTemplateProps {
    user: RPUser
    onSaveChangesClick: (newValues: Partial<RPUser>) => Promise<void>
    discardChanges(): void
    formikRef: React.RefObject<FormikProps<Partial<RPUser>>>
    formikState: FormikState<Partial<RPUser>>
    FormikStateBridge: React.FC
}

export const ManageProfilePageTemplate: FC<ManageProfilePageTemplateProps> = ({
    user,
    formikRef,
    onSaveChangesClick,
    discardChanges,
    formikState,
    FormikStateBridge,
}) => {
    const mdScreens = useWindowSizeUp('md')
    const { classes: localClasses } = useLocalStyles()
    const containerRef = useRef(null)

    const mdElement = useElementWidthUp(containerRef, 'md')
    const [profilePictureFormRef, scrollToProfilePictureForm] =
        useScrollHighlight()

    const ChangeCTAs = useMemo(
        () => (
            <div className={localClasses.changesCtas}>
                <Button
                    disabled={!formikState.dirty}
                    variant="outlined"
                    color="secondary"
                    onClick={discardChanges}
                >
                    Discard Changes
                </Button>
                <LoadingButton
                    disabled={!formikState.dirty || !formikState.isValid}
                    variant="contained"
                    color="primary"
                    onClickAsync={async () =>
                        await formikRef.current?.submitForm?.()
                    }
                >
                    Save Changes
                </LoadingButton>
            </div>
        ),
        [localClasses.changesCtas, discardChanges, formikState, formikRef]
    )

    const ProfileOverviewAndChangeCTAs = useMemo(
        () => (
            <div
                className={classNames(localClasses.header, {
                    [localClasses.mdHeader]: mdElement,
                })}
            >
                <ProfileOverview
                    user={user}
                    onChangeProfilePictureClick={scrollToProfilePictureForm}
                />
                {ChangeCTAs}
            </div>
        ),
        [
            ChangeCTAs,
            localClasses.header,
            localClasses.mdHeader,
            mdElement,
            user,
            scrollToProfilePictureForm,
        ]
    )

    return (
        <div className={localClasses.container} ref={containerRef}>
            <div className={localClasses.pageTitle}>
                <Typography variant={mdScreens ? 'h2' : 'h3'} component="h1">
                    Edit Profile
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                    Make changes to your ResiliencePoint profile here.
                </Typography>
                <Typography variant="body2" color={colors.primary.CERULEAN}>
                    <strong>Please note:</strong> if you would like to make
                    changes to your profile for a different organization, you
                    must be logged in to the organization that profile is
                    connected to.
                </Typography>
            </div>
            {ProfileOverviewAndChangeCTAs}
            <ProfileDetailsForm
                formikRef={formikRef}
                FormikStateBridge={FormikStateBridge}
                targetUser={user}
                onSubmit={onSaveChangesClick}
                mode={ProfileFormMode.MANAGE_SELF}
                profilePictureFormRef={profilePictureFormRef}
            />
            {ChangeCTAs}
        </div>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        margin: '0 auto',
    },
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '1rem 2rem',
        padding: '0 0 2rem',
        alignItems: 'flex-start',
    },
    mdHeader: {
        gridTemplateColumns: '1fr auto',
        width: '100%',
    },
    pageTitle: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    changesCtas: {
        display: 'flex',
        gap: '0.5rem',
        alignSelf: 'flex-end',
    },
    welcomeText: {
        display: 'flex',
        padding: '2rem 0 1rem',
    },
})
